import { CPopover } from "@coreui/react";
import {
  INVOICE_STATUS_APPROVED_BY_SET,
  INVOICE_STATUS_CANCELLATION_FAILED,
  INVOICE_STATUS_CANCELLATION_REQUESTED,
  INVOICE_STATUS_CANCELLED,
  INVOICE_STATUS_CHOICES,
  INVOICE_STATUS_DRAFT,
  INVOICE_STATUS_DRAFT_DISCARDED,
  INVOICE_STATUS_ERROR,
  INVOICE_STATUS_NOMINATED,
  INVOICE_STATUS_RECOVERABLE_ERROR,
  INVOICE_STATUS_RESEND_REQUESTED,
  INVOICE_STATUS_SET_UNKNOWN_ERROR,
  INVOICE_STATUS_UPLOADED_TO_SET,
  Invoice,
} from "../../models/invoice";
import { useEffect, useState } from "react";
import { useSub } from "../../hooks/pub-sub";
import DocumentError from "../../models/document-error";
import { getItem } from "../../api/generics";
import { SUCCESS } from "../../utils/constants/tags";

interface InvoiceCrudStatusProps {
  invoice: Invoice;
}

const InvoiceCrudStatus: React.FC<InvoiceCrudStatusProps> = ({ invoice }) => {
  const [currentInvoice, setCurrentInvoice] = useState(invoice);
  const [retrievedError, setRetrievedError] = useState<DocumentError>();
  const [loadingError, setLoadingError] = useState(false);

  useSub(`invoice_${invoice.id}`, (invoice: Invoice) => {
    setCurrentInvoice(invoice);
  });

  useEffect(() => {
    setCurrentInvoice(invoice);
  }, [invoice]);

  useEffect(() => {
    const fetchError = async () => {
      const errorCode = currentInvoice.setResponseCode;
      const errorMessage =
        currentInvoice.setResponseDescription || currentInvoice.setError;

      if (
        !(
          currentInvoice.status === INVOICE_STATUS_ERROR ||
          currentInvoice.status === INVOICE_STATUS_CANCELLATION_FAILED ||
          currentInvoice.status === INVOICE_STATUS_SET_UNKNOWN_ERROR
        )
      ) {
        setRetrievedError(undefined);
        return;
      }

      if (!errorCode && !errorMessage) {
        setRetrievedError(undefined);
        return;
      }

      setLoadingError(true);
      try {
        let result;
        if (errorCode) {
          const params = new Map<string, string | undefined>();
          params.set("error_code", errorCode);
          result = await getItem<DocumentError>(
            "/document_errors/search",
            params,
          );
        }

        if (
          (!result || result.status !== SUCCESS || !result.data) &&
          errorMessage
        ) {
          const params = new Map<string, string | undefined>();
          params.set("raw_message", errorMessage);
          result = await getItem<DocumentError>(
            "/document_errors/search",
            params,
          );
        }

        if (result?.status === SUCCESS && result.data) {
          setRetrievedError(result.data);
        } else {
          setRetrievedError(undefined);
        }
      } catch (err) {
        console.error("Error loading document error:", err);
        setRetrievedError(undefined);
      } finally {
        setLoadingError(false);
      }
    };

    fetchError();
  }, [currentInvoice]);

  let badgeClassName = "badge-info";

  if (currentInvoice.status === INVOICE_STATUS_UPLOADED_TO_SET) {
    badgeClassName = "badge-warning";
  }

  if (
    currentInvoice.status === INVOICE_STATUS_ERROR ||
    currentInvoice.status === INVOICE_STATUS_RECOVERABLE_ERROR
  ) {
    badgeClassName = "badge-danger";
  }

  if (currentInvoice.status === INVOICE_STATUS_SET_UNKNOWN_ERROR) {
    badgeClassName = "badge-danger";
  }

  if (currentInvoice.status === INVOICE_STATUS_APPROVED_BY_SET) {
    badgeClassName = "badge-success";
  }

  if (currentInvoice.status === INVOICE_STATUS_CANCELLATION_REQUESTED) {
    badgeClassName = "badge-light";
  }

  if (currentInvoice.status === INVOICE_STATUS_CANCELLED) {
    badgeClassName = "badge-info";
  }

  if (currentInvoice.status === INVOICE_STATUS_CANCELLATION_FAILED) {
    badgeClassName = "badge-danger";
  }

  if (currentInvoice.status === INVOICE_STATUS_RESEND_REQUESTED) {
    badgeClassName = "badge-warning";
  }

  if (currentInvoice.status === INVOICE_STATUS_NOMINATED) {
    badgeClassName = "badge-success";
  }

  if (currentInvoice.status === INVOICE_STATUS_DRAFT) {
    badgeClassName = "badge-secondary";
  }

  if (currentInvoice.status === INVOICE_STATUS_DRAFT_DISCARDED) {
    badgeClassName = "badge-dark";
  }

  const renderPopoverContent = () => {
    if (
      ![
        INVOICE_STATUS_ERROR,
        INVOICE_STATUS_CANCELLATION_FAILED,
        INVOICE_STATUS_SET_UNKNOWN_ERROR,
      ].includes(currentInvoice.status!)
    )
      return null;

    return (
      <ul
        style={{
          paddingLeft: "10px",
          paddingRight: "10px",
          paddingBottom: "0px",
          marginBottom: "0px",
        }}
      >
        {currentInvoice.setResponseCode && (
          <li>Código: {currentInvoice.setResponseCode}</li>
        )}
        {currentInvoice.setResponseDescription && (
          <li>Descripción: {currentInvoice.setResponseDescription}</li>
        )}
        {currentInvoice.setError && <li>Error: {currentInvoice.setError}</li>}
        {!loadingError && retrievedError && (
          <>
            {retrievedError.description && (
              <li>Significado: {retrievedError.description}</li>
            )}
            {retrievedError.possibleSolutions && (
              <li>Posible solución: {retrievedError.possibleSolutions}</li>
            )}
          </>
        )}
      </ul>
    );
  };

  return (
    <td className="text-center">
      <span
        className={`badge badge-pill ${badgeClassName}`}
        style={{ fontSize: "14px" }}
      >
        {INVOICE_STATUS_CHOICES.get(currentInvoice.status!)}{" "}
        {renderPopoverContent() ? (
          <CPopover
            content={renderPopoverContent()}
            placement="top"
            trigger="click"
          >
            <span
              className="fa fa-info-circle"
              style={{ cursor: "pointer" }}
            ></span>
          </CPopover>
        ) : null}
      </span>
    </td>
  );
};

export default InvoiceCrudStatus;
